import React, {
  ForwardedRef,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { Text, Button, Grid, Row, Col, FlexboxGrid } from 'rsuite';
import {
  selectConfigExpressRegistrationTypeVerbiage,
  selectConfigExpressRegistrationVerbiage,
  selectConfigExpressTaxEnabled,
} from '../../../store/slices/config/configSelectors';
import pluralize from 'pluralize';
import UsersLicenses from './UsersLicenses';
import { setCurrentStep } from '../../../store/slices/flow/flowSlice';
import {
  mapApplyFlowConfigToFlowSteps,
  setTaxSteps,
} from '../../../store/slices/flow/flowThunks';
import usePlaceNavigate from '../../../hooks/usePlaceNavigate';
import { BREAKPOINT_ENUM } from '../../../common/enums';
import { C_ACTIVE_PERMIT_STATUSES } from '../../../common/consts';
import { useAppDispatch } from 'src/store/store';
import { License } from 'src/types/licenses';

interface UsersLicensesWrapperProps {
  licenses: License[];
  loading?: boolean;
  onSizeCalculated?: () => void;
  errorMessage?: string;
}

const UsersLicensesWrapper = forwardRef(
  (
    {
      onSizeCalculated = () => null,
      licenses,
      loading,
      errorMessage,
    }: UsersLicensesWrapperProps,
    ref: ForwardedRef<HTMLDivElement>
  ) => {
    // approximatley the height of 4 rows
    // We want a min height becuase a user may have a single licnese with several actions
    // In the case, without a min height, the eaction list is too small in height
    const minHeight = '256px';
    const dispatch = useAppDispatch();
    const placeNavigate = usePlaceNavigate();
    const [listHeight, setListHeight] = useState(minHeight);

    const taxEnabled = useSelector(selectConfigExpressTaxEnabled);
    const registrationVerbiage = useSelector(
      selectConfigExpressRegistrationVerbiage
    );
    const typeVerbiage = useSelector(
      selectConfigExpressRegistrationTypeVerbiage
    );

    const updateHeight = useCallback(() => {
      if (
        window.innerWidth < BREAKPOINT_ENUM.MEDIUM ||
        licenses?.length === 0
      ) {
        setListHeight('auto');
      } else {
        setListHeight(minHeight);
      }
    }, [licenses]);

    useEffect(() => {
      window.addEventListener('resize', updateHeight);
      updateHeight();
      onSizeCalculated();
      return () => window.removeEventListener('resize', updateHeight);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updateHeight]);

    const numActiveLicensesText = useMemo(() => {
      // we only count the active licenses (status of submitted or current)
      const numActiveLicenses = (licenses ?? []).filter((license: License) =>
        C_ACTIVE_PERMIT_STATUSES.includes(license.status)
      ).length;

      return licenses ? `(${numActiveLicenses})` : '';
    }, [licenses]);

    const onFlowStepsMapped = useCallback(
      (path?: string) => {
        dispatch(setCurrentStep(1));
        placeNavigate(`/${path}`);
      },
      [dispatch, placeNavigate]
    );

    const onApplyForNewLicense = useCallback(() => {
      dispatch(mapApplyFlowConfigToFlowSteps(onFlowStepsMapped));
    }, [dispatch, onFlowStepsMapped]);

    const onInitiateTax = useCallback(() => {
      dispatch(setTaxSteps(onFlowStepsMapped));
    }, [dispatch, onFlowStepsMapped]);

    return (
      <FlexboxGrid
        role="grid"
        ref={ref}
      >
        <FlexboxGrid.Item
          role="row"
          colspan={24}
          className="mb-8"
        >
          <div role="cell">
            <Text
              as="h2"
              className="text-large text-bold"
            >{`Active ${pluralize(registrationVerbiage || '')} ${numActiveLicensesText}`}</Text>
          </div>
        </FlexboxGrid.Item>

        <FlexboxGrid.Item
          role="row"
          colspan={24}
          className="content-section height-100-percent"
          style={{ flex: 1 }}
        >
          <div role="cell">
            <Grid
              role="grid"
              fluid
            >
              <Row
                className="mb-16"
                style={{ minHeight: listHeight }}
              >
                <Col
                  role="cell"
                  xs={24}
                >
                  <UsersLicenses
                    licenses={licenses}
                    loading={loading}
                    errorMessage={errorMessage}
                  />
                </Col>
              </Row>
              <Row>
                <Col xs={24}>
                  <Grid fluid>
                    <FlexboxGrid
                      as={Row}
                      style={{ alignItems: 'stretch' }}
                    >
                      <FlexboxGrid.Item
                        as={Col}
                        xs={24}
                        md={8}
                        className="flex-row-md pb-lg-lower-8"
                      >
                        <Button
                          className="btn-secondary"
                          style={{ width: '100%', height: '100%' }}
                          onClick={onApplyForNewLicense}
                          data-cy="apply-for-new-license-button"
                        >
                          Apply for new {typeVerbiage}
                        </Button>
                      </FlexboxGrid.Item>

                      {taxEnabled && licenses.length > 0 && (
                        <FlexboxGrid.Item
                          as={Col}
                          xs={24}
                          md={8}
                          className="flex-row-md pb-lg-lower-8"
                        >
                          <Button
                            className="btn-secondary"
                            style={{ width: '100%', height: '100%' }}
                            onClick={onInitiateTax}
                            data-cy="pay-tax-button"
                          >
                            Pay Tax
                          </Button>
                        </FlexboxGrid.Item>
                      )}

                      {licenses.length > 0 && (
                        <FlexboxGrid.Item
                          as={Col}
                          xs={24}
                          md={8}
                          className="flex-row-md pb-lg-lower-8"
                        >
                          <Button
                            className="btn-secondary"
                            style={{ width: '100%', height: '100%' }}
                            onClick={() => {
                              placeNavigate('/all-permits');
                            }}
                            data-cy="see-all-licenses-button"
                          >
                            See all {pluralize(typeVerbiage)}
                          </Button>
                        </FlexboxGrid.Item>
                      )}
                    </FlexboxGrid>
                  </Grid>
                </Col>
              </Row>
            </Grid>
          </div>
        </FlexboxGrid.Item>
      </FlexboxGrid>
    );
  }
);

export default UsersLicensesWrapper;
